import { Alert, Box, Typography, useMediaQuery, type Theme } from '@mui/material';
import { RefreshCw } from 'lucide-react';
import { useCallback, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactElement, type SyntheticEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { createAdminApi, type AdminDataRecord, type AdminPage, roleSatisfies } from '@/admin/adminApi';
import { useHttpClient } from '@/app/providers';
import { AccessPanel } from '@/admin/users/accountsUi';
import { useAdminAccess } from '@/admin/users/useAdminAccess';
import {
  AccessDenied,
  Avatar,
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  Icon,
  LoadingState,
  MasterDetail,
  PageHeader,
  displayValue,
  useDetailRoute,
  type DataTableColumn,
} from '@/ui/kit';

import { DEFAULT_COLLECTION_TYPE, collectionFor, orderedCollections, type ExplorerCollection } from './collections';
import { MISSING, formatCompactInstant, isRecord, stringOf } from './payload';
import { endOfDayAtom, periodError, startOfDayAtom } from './period';
import { presentRecord } from './presenters';
import { RecordDetail } from './RecordDetail';
import { dataScopePath, isScopeId, scopeFromPath } from './routes';
import { useAdminQuery, useRefreshOnReturn } from './useAdminQuery';

/**
 * Explorateur de donnees (P08) : ce que le serveur a recu d'une famille.
 *
 * Il lit le **journal de synchronisation**, pas les tables de domaine (ADR 0035
 * §1) : seul endroit ou vivent les saisies des parents. Cinq partis pris, chacun
 * dit sur l'ecran :
 *
 * 1. **Lecture seule, et vue du serveur.** Aucun bouton d'ecriture n'existe ici ;
 *    et ce que l'appareil n'a pas encore synchronise (son outbox) n'y est pas.
 * 2. **Tout nombre vient du serveur.** Compteurs des collections et total du
 *    tableau sont des `COUNT(*)` ; un compteur absent est « — », jamais zero.
 *    Le compteur inclut les suppressions logiques faites sur l'appareil.
 * 3. **Aucune relecture automatique.** « Actualiser » et le retour sur l'onglet
 *    relisent ; chaque lecture ecrit une ligne d'audit (ADR 0035 §4). Appliquer
 *    une periode ou choisir une collection est un geste, pas une frappe.
 * 4. **Un role insuffisant, un compte anonymise, un profil jamais synchronise ne
 *    sont pas des pannes** : trois phrases distinctes, aucune deconnexion.
 * 5. **Le perimetre est un identifiant dans l'URL** (`/data/{babyId}`). Le
 *    selecteur Compte → Profil viendra avec la liste des familles (#514) ; il
 *    prendra la place marquee plus bas. Les tirages, lieux, lots et mouvements
 *    sont ranges par le mobile sous l'identifiant du **compte** : ils se lisent en
 *    ouvrant l'identifiant du compte, non celui du bebe.
 */

const PER_PAGE = 25;

const pageCount = (total: number, perPage: number): number => Math.max(1, Math.ceil(total / perPage));

const intParam = (value: string | null, fallback: number): number => {
  const parsed = Number(value);

  return Number.isInteger(parsed) && parsed >= 1 ? parsed : fallback;
};

/** « 8 mois » depuis une date de naissance `AAAA-MM-JJ` ; `null` si elle est illisible ou future. */
export const ageLabel = (birthDate: string | null, now: Date): string | null => {
  const match = birthDate === null ? null : /^(\d{4})-(\d{2})-(\d{2})/.exec(birthDate);

  if (match === null) {
    return null;
  }

  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  let months = (now.getFullYear() - year) * 12 + (now.getMonth() + 1 - month);

  if (now.getDate() < day) {
    months -= 1;
  }

  if (months < 0) {
    return null;
  }

  if (months < 1) {
    return 'moins d’un mois';
  }

  if (months < 24) {
    return `${String(months)} mois`;
  }

  return `${String(Math.floor(months / 12))} ans`;
};

// ---------------------------------------------------------------------------
// Perimetre
// ---------------------------------------------------------------------------

const ScopeEntry = ({ initialError }: { initialError?: string }): ReactElement => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | undefined>(initialError);

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();

    const value = draft.trim();

    if (!isScopeId(value)) {
      setError('Saisissez un identifiant complet (format UUID), copié depuis la fiche d’un compte.');

      return;
    }

    void navigate(dataScopePath(value));
  };

  return (
    <Card title="Choisir un périmètre" subtitle="Un profil bébé, ou un compte pour ses tirages, lieux, lots et mouvements.">
      {/* Sélecteur Compte → Profil : BO-17 (issue 514), à poser ici dès que la liste des familles existe. */}
      <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 320px', maxWidth: 520 }}>
          <Field
            label="Identifiant du profil bébé ou du compte"
            value={draft}
            onChange={(value) => {
              setDraft(value);
              setError(undefined);
            }}
            error={error}
            hint="L’identifiant s’ouvre aussi par un lien direct : /data/{identifiant}."
            autoComplete="off"
          />
        </Box>
        <Box sx={{ pt: '28px' }}>
          <Button type="submit" variant="primary">
            Ouvrir
          </Button>
        </Box>
      </Box>
    </Card>
  );
};

const ScopeHeader = ({ babyId, header }: { babyId: string; header: AdminDataRecord | null }): ReactElement => {
  const profile = header === null || !isRecord(header.payload) ? null : (header.payload.profile ?? null);
  const record = isRecord(profile) ? profile : null;
  const firstName = stringOf(record, 'firstName');
  const age = ageLabel(stringOf(record, 'birthDate'), new Date());

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      <Avatar name={firstName ?? '?'} size={40} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 800 }}>
          {firstName === null ? 'Périmètre sans profil bébé' : `${firstName}${age === null ? '' : ` · ${age}`}`}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'tokens.textMuted', overflowWrap: 'anywhere' }}>
          {firstName === null ? 'Aucun profil dans le journal pour cet identifiant · ' : ''}
          {babyId}
        </Typography>
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Colonne des collections (listbox)
// ---------------------------------------------------------------------------

const CollectionList = ({
  items,
  selected,
  onSelect,
  orientation,
}: {
  items: readonly { readonly collection: ExplorerCollection; readonly count: number | null }[];
  selected: string;
  onSelect: (type: string) => void;
  orientation: 'vertical' | 'horizontal';
}): ReactElement => {
  const refs = useRef<(HTMLElement | null)[]>([]);
  const [focused, setFocused] = useState<number | null>(null);
  const selectedIndex = Math.max(0, items.findIndex((item) => item.collection.type === selected));
  const tabbable = focused ?? selectedIndex;

  const move = (event: KeyboardEvent<HTMLElement>, index: number): void => {
    const last = items.length - 1;
    // Les deux paires de fleches deplacent le focus, quelle que soit l'orientation : la liste change de sens avec la largeur.
    const forward = event.key === 'ArrowDown' || event.key === 'ArrowRight';
    const backward = event.key === 'ArrowUp' || event.key === 'ArrowLeft';
    const next = forward ? Math.min(last, index + 1) : backward ? Math.max(0, index - 1) : event.key === 'Home' ? 0 : event.key === 'End' ? last : null;

    if (next !== null) {
      event.preventDefault();
      setFocused(next);
      refs.current[next]?.focus();

      return;
    }

    // Choisir une collection est une lecture : un geste (Entree, Espace), pas le simple deplacement du focus.
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(items[index]?.collection.type ?? selected);
    }
  };

  return (
    <Box
      role="listbox"
      aria-label="Collections"
      aria-orientation={orientation}
      sx={orientation === 'vertical' ? { display: 'grid', gap: 0.25 } : { display: 'flex', gap: 0.5, overflowX: 'auto', pb: 0.5 }}
    >
      {items.map(({ collection, count }, index) => {
        const active = collection.type === selected;

        return (
          <Box
            key={collection.type}
            role="option"
            aria-selected={active}
            tabIndex={index === tabbable ? 0 : -1}
            ref={(node: HTMLElement | null) => {
              refs.current[index] = node;
            }}
            onClick={() => {
              onSelect(collection.type);
            }}
            onKeyDown={(event) => {
              move(event, index);
            }}
            onFocus={() => {
              setFocused(index);
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.25,
              py: 1,
              minHeight: 40,
              flex: orientation === 'horizontal' ? 'none' : undefined,
              whiteSpace: 'nowrap',
              borderRadius: '10px',
              cursor: 'pointer',
              color: active ? 'tokens.accentPinkStrong' : 'tokens.textStrong',
              bgcolor: active ? 'tokens.primarySoft' : 'transparent',
              fontWeight: active ? 700 : 600,
              fontSize: 14,
              '&:hover': { bgcolor: active ? 'tokens.primarySoft' : 'tokens.surfaceMuted' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'tokens.primary', outlineOffset: -2 },
            }}
          >
            <Icon icon={collection.icon} size={18} />
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              {collection.label}
            </Box>
            <Box component="span" sx={{ fontSize: 12, fontWeight: 700, color: 'tokens.textMuted' }}>
              {displayValue(count)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Tableau
// ---------------------------------------------------------------------------

const columnsFor = (collection: ExplorerCollection): readonly DataTableColumn<AdminDataRecord>[] => {
  const presented = (record: AdminDataRecord) => presentRecord(record.entityType, record.payload, record.redactedFields).summary;

  return [
    {
      key: 'when',
      header: 'Quand',
      primary: true,
      render: (record) => formatCompactInstant(presented(record).when ?? record.updatedAt),
    },
    ...(collection.hasNature
      ? [
          {
            key: 'nature',
            header: 'Nature',
            render: (record: AdminDataRecord): ReactElement | string => {
              const nature = presented(record).nature;

              return nature === null ? MISSING : <Badge label={nature.label} tone={nature.tone} />;
            },
          },
        ]
      : []),
    { key: 'detail', header: 'Détail', render: (record) => presented(record).detail },
    {
      key: 'version',
      header: 'Version serveur',
      align: 'right',
      render: (record) => String(record.version),
    },
  ];
};

const PageControls = ({
  page,
  total,
  perPage,
  busy,
  onPage,
}: {
  page: AdminPage<AdminDataRecord>;
  total: number;
  perPage: number;
  busy: boolean;
  onPage: (next: number) => void;
}): ReactElement => {
  const pages = pageCount(total, perPage);
  const label = total < 0 ? 'Nombre total non communiqué par le serveur.' : `${total.toLocaleString('fr-FR')} enregistrement${total > 1 ? 's' : ''} · page ${String(page.page)} sur ${String(pages)}`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mt: 2 }}>
      <Button
        size="S"
        disabled={page.page <= 1 || busy}
        onClick={() => {
          onPage(page.page - 1);
        }}
      >
        Page précédente
      </Button>
      <Typography role="status" sx={{ fontSize: 13, color: 'tokens.textMuted', textAlign: 'center' }}>
        {label}
      </Typography>
      <Button
        size="S"
        disabled={(total >= 0 ? page.page >= pages : page.items.length < perPage) || busy}
        onClick={() => {
          onPage(page.page + 1);
        }}
      >
        Page suivante
      </Button>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Ecran
// ---------------------------------------------------------------------------

const Explorer = ({
  babyId,
  canSeeFreeText,
  token,
  refresh,
}: {
  babyId: string;
  canSeeFreeText: boolean;
  token: number;
  refresh: () => void;
}): ReactElement => {
  const http = useHttpClient();
  const [params, setParams] = useSearchParams();
  const detailRoute = useDetailRoute(dataScopePath(babyId));
  const navigate = useNavigate();

  const type = params.get('collection') ?? DEFAULT_COLLECTION_TYPE;
  const pageNumber = intParam(params.get('page'), 1);
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const draftError = periodError(draftFrom, draftTo);
  const collection = collectionFor(type);
  const appliedError = periodError(from, to);

  const api = useMemo(() => createAdminApi(http), [http]);
  const collections = useAdminQuery(`collections|${babyId}`, () => api.listCollections(babyId), { token });
  const anonymized = collections.failure?.anonymized === true;
  const forbidden = collections.failure?.forbidden === true;
  const blocked = anonymized || forbidden;

  const records = useAdminQuery(
    `records|${babyId}|${type}|${String(pageNumber)}|${from}|${to}`,
    () =>
      api.listRecords(babyId, {
        type,
        page: pageNumber,
        perPage: PER_PAGE,
        updatedFrom: startOfDayAtom(from),
        updatedTo: endOfDayAtom(to),
      }),
    // Apres les compteurs : un perimetre refuse (403) ou anonymise (410) ne
    // doit produire qu'une seule ligne d'audit, pas une par requete lancee en vain.
    { token, enabled: collections.data !== null && appliedError === null },
  );

  const hasProfile = (collections.data ?? []).some((item) => item.type === 'baby_profile' && item.count > 0);
  const header = useAdminQuery(
    `header|${babyId}`,
    async () => (await api.listRecords(babyId, { type: 'baby_profile', page: 1, perPage: 1 })).items[0] ?? null,
    { token, enabled: hasProfile && !blocked },
  );

  const update = (changes: Record<string, string | null>): void => {
    const next = new URLSearchParams(params);

    for (const [key, value] of Object.entries(changes)) {
      if (value === null || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }

    setParams(next);
  };

  // Changer de collection ferme aussi le tiroir : l'enregistrement ouvert n'est pas de la nouvelle.
  const selectCollection = (nextType: string): void => {
    const next = new URLSearchParams(params);

    next.set('collection', nextType);
    next.delete('page');
    void navigate({ pathname: dataScopePath(babyId), search: `?${next.toString()}` });
  };

  const applyPeriod = (event: SyntheticEvent): void => {
    event.preventDefault();

    if (draftError === null) {
      update({ from: draftFrom, to: draftTo, page: null });
    }
  };

  const clearPeriod = (): void => {
    setDraftFrom('');
    setDraftTo('');
    update({ from: null, to: null, page: null });
  };

  const items = useMemo(() => orderedCollections(collections.data), [collections.data]);
  const everyCollectionEmpty = collections.data?.every((item) => item.count === 0) === true;
  const page = records.data;
  const rows = page?.items ?? [];
  const failure = records.failure;
  const periodApplied = from !== '' || to !== '';
  const listId = useId();

  // Colonne a gauche tant qu'il y a la place ; sinon une rangee au-dessus du tableau.
  // Tiroir ouvert, la liste perd 480 a 560 px (`MasterDetail`) : une colonne de
  // 210 px ne laisserait au tableau qu'une trentaine de caracteres par ligne.
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const roomy = useMediaQuery('(min-width:1700px)');
  const orientation = mdUp && (detailRoute.selectedId === null || roomy) ? 'vertical' : 'horizontal';

  const list = (
    <Box sx={{ display: 'grid', gridTemplateColumns: orientation === 'vertical' ? '210px minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: 2, alignItems: 'start' }}>
      <Card title="Collections" subtitle="Comptes réels du serveur, suppressions faites sur l’appareil comprises.">
        {collections.failure !== null && collections.data === null ? (
          <ErrorState message={collections.failure.message} onRetry={refresh} />
        ) : (
          <CollectionList items={items} selected={type} onSelect={selectCollection} orientation={orientation} />
        )}
      </Card>

      <Card
        title={collection.label}
        subtitle="Du plus récemment reçu au plus ancien. Un appui ouvre le détail."
        actions={records.busy ? <Typography role="status" sx={{ fontSize: 12, color: 'tokens.textMuted' }}>Chargement…</Typography> : undefined}
      >
        <div id={listId}>
          {appliedError !== null ? <ErrorState title="Période invalide" message={appliedError} onRetry={clearPeriod} /> : null}
          {failure !== null && !failure.forbidden && !failure.anonymized ? (
            <ErrorState
              message={failure.notFound ? 'Collection inconnue du serveur.' : failure.message}
              onRetry={refresh}
            />
          ) : null}
          {failure?.anonymized === true ? (
            <AccessDenied title="Compte anonymisé : ses données ne sont plus consultables">
              Le rattachement de ce périmètre a changé depuis la lecture des compteurs : le serveur ne rend plus rien de ce qui a été écrit par un compte anonymisé.
            </AccessDenied>
          ) : null}
          {failure?.forbidden === true ? (
            <AccessDenied title="Votre rôle ne permet pas de consulter ces données">
              Votre compte reste connecté : il manque le rôle d’administration requis pour lire cette collection.
            </AccessDenied>
          ) : null}
          {page === null && failure === null && appliedError === null ? <LoadingState label="Chargement des enregistrements…" /> : null}
          {page !== null && rows.length === 0 ? (
            <EmptyState
              title={periodApplied ? 'Aucun enregistrement sur cette période' : 'Aucun enregistrement reçu par le serveur'}
              description={
                periodApplied
                  ? 'Aucune version de cette collection n’a été reçue par le serveur entre ces deux dates.'
                  : everyCollectionEmpty
                    ? 'Ce périmètre n’a encore rien synchronisé : ce n’est pas une erreur. Les saisies qui attendent sur un appareil n’apparaissent pas ici.'
                    : `Le serveur n’a encore reçu aucun enregistrement de la collection « ${collection.label} » pour ce périmètre.`
              }
            />
          ) : null}
          {page !== null && rows.length > 0 ? (
            <>
              <DataTable<AdminDataRecord>
                label={`Enregistrements : ${collection.label}`}
                columns={columnsFor(collection)}
                rows={rows}
                getRowId={(record) => record.entityId}
                selectedRowId={detailRoute.selectedId}
                onRowSelect={(record) => {
                  detailRoute.open(record.entityId);
                }}
              />
              <PageControls
                page={page}
                total={page.total}
                perPage={page.perPage}
                busy={records.busy}
                onPage={(next) => {
                  update({ page: next <= 1 ? null : String(next) });
                }}
              />
            </>
          ) : null}
        </div>
      </Card>
    </Box>
  );

  return (
    <>
      {blocked ? null : (
        <Card>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <ScopeHeader babyId={babyId} header={header.data} />
            <Box
              component="form"
              role="search"
              aria-label="Période de réception"
              onSubmit={applyPeriod}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}
            >
              {/* Sélecteur Compte → Profil : BO-17 (issue 514), ici, à gauche de la période. */}
              <Field label="Reçu par le serveur · du" type="date" value={draftFrom} onChange={setDraftFrom} error={draftError ?? undefined} />
              <Field label="au" type="date" value={draftTo} onChange={setDraftTo} />
              <Button type="submit" variant="primary" disabled={draftError !== null}>
                Appliquer
              </Button>
              {periodApplied || draftFrom !== '' || draftTo !== '' ? (
                <Button type="button" onClick={clearPeriod}>
                  Effacer
                </Button>
              ) : null}
            </Box>
          </Box>
        </Card>
      )}

      {anonymized ? (
        <AccessDenied title="Compte anonymisé : ses données ne sont plus consultables">
          Plus aucun compte actif n’est rattaché à ce périmètre. Le serveur ne rend rien de ce qui a été écrit par un compte anonymisé.
        </AccessDenied>
      ) : forbidden ? (
        <AccessDenied title="Votre rôle ne permet pas de consulter ces données">
          Votre compte reste connecté : il manque le rôle d’administration requis pour lire le journal de ce périmètre.
        </AccessDenied>
      ) : (
        <>
          {canSeeFreeText ? null : (
            <Alert severity="info" variant="outlined" role="note">
              Les champs de texte libre (commentaires, notes) sont masqués pour votre rôle.
            </Alert>
          )}
          {collections.busy && collections.data === null && collections.failure === null ? <LoadingState label="Chargement des collections…" /> : null}
          <MasterDetail
            list={list}
            selectedId={detailRoute.selectedId}
            detailLabel="Détail de l’enregistrement"
            onClose={detailRoute.close}
            renderDetail={(entityId) => (
              <RecordDetail
                key={`${type}|${entityId}`}
                babyId={babyId}
                type={type}
                entityId={entityId}
                collectionLabel={collection.label}
                record={rows.find((record) => record.entityId === entityId) ?? null}
                token={token}
                onRetry={refresh}
              />
            )}
          />
        </>
      )}
    </>
  );
};

export const DataExplorerScreen = (): ReactElement => {
  const { pathname } = useLocation();
  const { access, reload } = useAdminAccess();
  const scope = scopeFromPath(pathname);
  const invalidScope = scope !== null && !isScopeId(scope);
  const granted = access.kind === 'granted';
  // Jeton de relecture : « Actualiser », « Reessayer » et le retour sur l'onglet
  // relisent d'un meme geste tout ce que l'ecran montre (ADR 0035 §4).
  const [token, setToken] = useState(0);
  const refresh = useCallback(() => {
    setToken((previous) => previous + 1);
  }, []);

  useRefreshOnReturn(refresh);

  return (
    <Box sx={{ display: 'grid', gap: 2.5, mt: 0.5, mb: 4 }}>
      <PageHeader
        eyebrow="Données"
        title="Explorateur de données"
        subtitle="Ce que le serveur a reçu d’une famille, en lecture seule. Une correction passe par l’application ou par un mouvement tracé (ADR 0011)."
        badges={<Badge label="Lecture seule" tone="info" dot />}
        actions={
          scope !== null && !invalidScope && granted ? (
            <Button icon={RefreshCw} onClick={refresh}>
              Actualiser
            </Button>
          ) : undefined
        }
      />

      <Alert severity="info" variant="outlined" role="note">
        Vue du serveur : les saisies pas encore synchronisées ne figurent pas ici.
      </Alert>

      <AccessPanel access={access} onRetry={reload} />

      {granted ? (
        scope === null || invalidScope ? (
          <ScopeEntry initialError={invalidScope ? 'Cet identifiant n’est pas valide : saisissez un UUID complet.' : undefined} />
        ) : (
          <Explorer key={scope} babyId={scope} canSeeFreeText={roleSatisfies(access.role, 'admin')} token={token} refresh={refresh} />
        )
      ) : null}
    </Box>
  );
};
